// doctor-frontend/src/services/http.js
// Backward-compatible tenant-aware HTTP helper.
// No-regression patch:
// - Preserves default `http` export, owner/user contexts, raw responses, JSON helpers, timeouts, params, cache-bust,
//   tenant-aware routing, auth error events, and existing named exports.
// - Adds stronger API-base fallback handling for REACT_APP_API_BASE_URL, REACT_APP_API_BASE, runtime overrides,
//   localhost :3000 → backend :5000, and production fallback.
// - Hardens user-token selection so doctor-frontend does not accidentally use owner/citizen tokens.
// - Sends X-Tenant-Id only when it is a real Mongo ObjectId; never sends tenant slug as tenant ID.
// - Keeps public/bootstrap and owner endpoints non-tenant-scoped.
// - Adds http.open() URL helper used by upload/file helpers.
// - Keeps doctor portal, owner portal, citizen portal, roster, guided activity, and OPD visit flows separated.
// July 2026 alignment:
// - Supports caller AbortSignal values without misreporting navigation cancellation as a timeout.
// - Keeps `cmfm_token` canonical while preserving existing token aliases.
// - Reads the new `cmfm_user` compatibility payload written by CMFMLoginRegister.
// - Adds response.data compatibility on HTTP errors while preserving access to the native Response.
// - Prevents stale timeout/listener leaks and keeps request cancellation observable by callers.

const RAW_BASE = (() => {
  try {
    const env =
      typeof process !== "undefined" && process?.env
        ? process.env
        : {};

    return String(
      env.REACT_APP_API_BASE_URL ||
        env.REACT_APP_API_BASE ||
        ""
    ).trim();
  } catch {
    return "";
  }
})();

const PROD_BACKEND_FALLBACK = "https://aj-portal-backend-demo.onrender.com";
const DEFAULT_TIMEOUT_MS = 30000;

/* ----------------------------- Safe helpers ----------------------------- */
function safeJsonParse(value) {
  try {
    return JSON.parse(value || "null");
  } catch {
    return null;
  }
}

const USER_PAYLOAD_KEYS = [
  "loggedInUser",
  "cmfm_user",
  "cmfmUser",
  "user",
  "currentUser",
  "authUser",
  "portalUser",
];

function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const key = store.key(i);
      if (key) out.push(key);
    }
    return out;
  } catch {
    return [];
  }
}

function isObjectId(value) {
  return typeof value === "string" && /^[a-f\d]{24}$/i.test(value.trim());
}

function normalizeBaseUrl(value) {
  const s = String(value || "").trim().replace(/\/+$/, "");
  return s;
}

function isLocalCraWindow() {
  try {
    const { hostname, port } = window.location || {};
    return (
      (hostname === "localhost" || hostname === "127.0.0.1") &&
      String(port) === "3000"
    );
  } catch {
    return false;
  }
}

function localBackendBaseFromWindow() {
  try {
    const { hostname, protocol } = window.location || {};
    return `${protocol}//${hostname}:5000`;
  } catch {
    return "";
  }
}

/* ------------------------ API base (dev/prod safe) ------------------------ */
function readRuntimeApiBase() {
  try {
    const pick = (store) =>
      normalizeBaseUrl(
        store.getItem("apiBaseUrl") ||
          store.getItem("apiBase") ||
          store.getItem("API_BASE_URL") ||
          store.getItem("API_BASE") ||
          ""
      );

    const raw = pick(localStorage) || pick(sessionStorage);
    if (raw && !raw.startsWith("/")) return raw;
  } catch {}

  return "";
}

function computeApiBase() {
  const cleaned = normalizeBaseUrl(RAW_BASE);

  // Relative env values such as "/api" get resolved against frontend :3000 in CRA.
  // Treat them as unset so the local/backend fallback can work.
  if (cleaned && cleaned.startsWith("/")) {
    return isLocalCraWindow() ? localBackendBaseFromWindow() : "";
  }

  // If env explicitly points to CRA dev server, force backend :5000.
  if (cleaned && isLocalCraWindow()) {
    try {
      const { hostname, protocol, origin } = window.location || {};
      const craOrigin = origin || `${protocol}//${hostname}:3000`;

      if (
        cleaned.startsWith(craOrigin) ||
        /^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(cleaned)
      ) {
        return localBackendBaseFromWindow();
      }
    } catch {}
  }

  if (cleaned) return cleaned;

  const runtimeBase = readRuntimeApiBase();
  if (runtimeBase) {
    if (
      isLocalCraWindow() &&
      /^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(runtimeBase)
    ) {
      return localBackendBaseFromWindow();
    }
    return runtimeBase;
  }

  // Local CRA fallback: frontend :3000, backend :5000.
  if (isLocalCraWindow()) return localBackendBaseFromWindow();

  // Production safeguard: prevent relative /api calls from hitting the static frontend
  // when env variables are missing in a deployed bundle.
  return PROD_BACKEND_FALLBACK;
}

const API_BASE = computeApiBase();

/* ----------------------------- Tenant helpers ----------------------------- */
function persistTenantSlug(slug) {
  try {
    const s = String(slug || "").trim();
    if (!s) return;
    localStorage.setItem("tenantSlug", s);
    localStorage.setItem("activeTenantSlug", s);
    sessionStorage.setItem("tenantSlug", s);
    sessionStorage.setItem("activeTenantSlug", s);
  } catch {}
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem("tenantSlug"),
      localStorage.getItem("activeTenantSlug"),
      sessionStorage.getItem("tenantSlug"),
      sessionStorage.getItem("activeTenantSlug"),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    USER_PAYLOAD_KEYS
      .flatMap((key) => [
        safeJsonParse(localStorage.getItem(key)),
        safeJsonParse(sessionStorage.getItem(key)),
      ])
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {}

  return (
    candidates
      .map((value) => String(value || "").trim())
      .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || ""
  );
}

function getTenantSlug() {
  try {
    const match = (window.location.pathname || "").match(/^\/t\/([^/]+)/i);
    if (match) {
      const slug = decodeURIComponent(match[1]);
      persistTenantSlug(slug);
      return slug;
    }
  } catch {}

  return readTenantSlugFromStorage();
}

function readTenantIdFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem("tenantId"),
      localStorage.getItem("activeTenantId"),
      sessionStorage.getItem("tenantId"),
      sessionStorage.getItem("activeTenantId"),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    USER_PAYLOAD_KEYS
      .flatMap((key) => [
        safeJsonParse(localStorage.getItem(key)),
        safeJsonParse(sessionStorage.getItem(key)),
      ])
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantId,
          obj?.tenantId,
          core?.tenant?._id,
          core?.tenant?.id,
          obj?.tenant?._id,
          obj?.tenant?.id
        );
      });
  } catch {}

  return candidates.map((x) => String(x || "").trim()).find(isObjectId) || "";
}

function hasTenantPrefix(path) {
  return /^\/t\/[^/]+(\/|$)/.test(String(path || ""));
}

function withTenantPath(path) {
  const cleanPath = String(path || "").startsWith("/")
    ? String(path || "")
    : `/${path || ""}`;

  if (hasTenantPrefix(cleanPath)) return cleanPath;

  const slug = getTenantSlug();
  if (!slug) return cleanPath;

  return `/t/${encodeURIComponent(slug)}${cleanPath}`.replace(/\/{2,}/g, "/");
}

// Endpoints that should never be nested under /t/:slug.
function isNonTenantApiPath(path) {
  const p = String(path || "");
  return (
    p.startsWith("/api/tenants") ||
    p.startsWith("/api/tenant") ||
    p.startsWith("/api/public") ||
    p.startsWith("/api/health") ||
    p.startsWith("/api/ping") ||
    p.startsWith("/api/status") ||
    p.startsWith("/api/auth/bootstrap")
  );
}

function isOwnerApiPath(path) {
  const p = String(path || "");
  return p.startsWith("/api/owner") || p.includes("/api/owner/");
}

/* ---------------------------- Token management ---------------------------- */
function readRoleFromPayload(payload) {
  const core = payload?.user || payload || {};
  return String(
    core?.role ||
      payload?.role ||
      core?.userType ||
      payload?.userType ||
      ""
  )
    .toLowerCase()
    .trim();
}

function tokenFromPayload(payload, { rejectCitizen = true } = {}) {
  if (!payload) return "";

  const role = readRoleFromPayload(payload);
  if (rejectCitizen && role === "citizen") return "";

  const core = payload.user || payload;
  const token = String(
    core?.token ||
      payload?.token ||
      core?.jwt ||
      payload?.jwt ||
      core?.accessToken ||
      payload?.accessToken ||
      core?.authToken ||
      payload?.authToken ||
      ""
  ).trim();

  return token && token !== "undefined" && token !== "null" ? token : "";
}

function readTokenFromNamedPayloads(store, opts = {}) {
  try {
    for (const key of USER_PAYLOAD_KEYS) {
      const token = tokenFromPayload(safeJsonParse(store.getItem(key)), opts);
      if (token) return token;
    }
  } catch {}

  return "";
}

function pickUserTokenFromStore(store) {
  try {
    const keys = listStorageKeys(store);

    const preferred = [
      "cmfm_token",
      "cmfmToken",
      "tenant_token",
      "tenantToken",
      "portal_token",
      "portalToken",
      "cmfm_jwt",
      "authToken",
      "accessToken",
      "jwt",
      "token",
    ];

    for (const name of preferred) {
      const hit = keys.find((key) => {
        const lower = String(key || "").toLowerCase();
        return (
          lower === String(name).toLowerCase() &&
          !/owner|citizen/i.test(key)
        );
      });
      if (hit) {
        const token = String(store.getItem(hit) || "").trim();
        if (token && token !== "undefined" && token !== "null") return token;
      }
    }

    const tenantish = keys.find(
      (key) =>
        /cmfm|tenant|portal/i.test(key) &&
        /token|jwt|access/i.test(key) &&
        !/owner|citizen/i.test(key)
    );
    if (tenantish) {
      const token = String(store.getItem(tenantish) || "").trim();
      if (token && token !== "undefined" && token !== "null") return token;
    }

    const generic = keys.find(
      (key) =>
        /token|jwt|access/i.test(key) &&
        !/owner|citizen/i.test(key)
    );
    if (generic) {
      const token = String(store.getItem(generic) || "").trim();
      if (token && token !== "undefined" && token !== "null") return token;
    }

    return readTokenFromNamedPayloads(store, { rejectCitizen: true });
  } catch {
    return "";
  }
}

function pickUserToken() {
  return (
    pickUserTokenFromStore(localStorage) ||
    pickUserTokenFromStore(sessionStorage) ||
    ""
  );
}

function pickOwnerToken() {
  try {
    return (
      localStorage.getItem("ownerToken") ||
      sessionStorage.getItem("ownerToken") ||
      localStorage.getItem("mededtekOwnerToken") ||
      sessionStorage.getItem("mededtekOwnerToken") ||
      ""
    );
  } catch {
    return "";
  }
}

/* -------------------------------- URL join ------------------------------- */
function buildPathForContext(url, opts = {}) {
  if (/^https?:\/\//i.test(url)) return url;

  const path = String(url || "").startsWith("/")
    ? String(url || "")
    : `/${url || ""}`;
  const ownerContext = !!opts.isOwnerApi || isOwnerApiPath(path);

  if (ownerContext || isNonTenantApiPath(path)) return path;

  return withTenantPath(path);
}

function joinUrl(url, opts = {}) {
  if (/^https?:\/\//i.test(url)) return url;

  const finalPath = buildPathForContext(url, opts);

  if (API_BASE) {
    return `${API_BASE.replace(/\/$/, "")}${finalPath}`;
  }

  return finalPath;
}

function getUrlResolutionBase() {
  try {
    return window.location.origin || API_BASE || PROD_BACKEND_FALLBACK;
  } catch {
    return API_BASE || PROD_BACKEND_FALLBACK;
  }
}

function appendParams(url, params) {
  const urlObj = new URL(url, getUrlResolutionBase());
  const sp = urlObj.searchParams;

  if (params && typeof params === "object") {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;

      if (Array.isArray(value)) {
        sp.delete(key);
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== "") {
            sp.append(key, String(item));
          }
        });
        return;
      }

      sp.set(key, String(value));
    });
  }

  return urlObj;
}

/* ------------------------ Quick, visible error helper ----------------------- */
function surface(message) {
  try {
    window.dispatchEvent(
      new CustomEvent("app:error", {
        detail: { message },
      })
    );
  } catch {}

  try {
    console.error("[HTTP]", message);
  } catch {}
}

function dispatchUnauthorized(ownerContext, detail) {
  try {
    const eventName = ownerContext
      ? "owner:unauthorized"
      : "auth:unauthorized";

    window.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
      })
    );
  } catch {}
}

function shouldParseAsJson(response) {
  const contentType = String(
    response?.headers?.get?.("content-type") || ""
  ).toLowerCase();

  return (
    contentType.includes("application/json") ||
    contentType.includes("+json")
  );
}

async function readResponsePayload(response) {
  if (!response) return null;

  try {
    if (shouldParseAsJson(response)) {
      return await response.json();
    }

    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
}
function attachPayloadToResponse(response, payload) {
  if (!response) return response;

  try {
    Object.defineProperty(response, "data", {
      configurable: true,
      enumerable: false,
      writable: false,
      value: payload,
    });

    return response;
  } catch {
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      headers: response.headers,
      data: payload,
      raw: response,
    };
  }
}

function normalizeTimeout(value) {
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_TIMEOUT_MS;
}

function createAbortContext(externalSignal, timeoutMs) {
  const controller = new AbortController();
  const effectiveTimeoutMs = normalizeTimeout(timeoutMs);

  let timedOut = false;
  let externallyAborted = false;

  const abortFromExternalSignal = () => {
    externallyAborted = true;

    try {
      controller.abort(externalSignal?.reason);
    } catch {
      controller.abort();
    }
  };

  if (externalSignal?.aborted) {
    abortFromExternalSignal();
  } else if (externalSignal?.addEventListener) {
    externalSignal.addEventListener(
      "abort",
      abortFromExternalSignal,
      { once: true }
    );
  }

  const timeoutId = setTimeout(() => {
    timedOut = true;

    try {
      controller.abort(
        new DOMException("Request timed out", "TimeoutError")
      );
    } catch {
      controller.abort();
    }
  }, effectiveTimeoutMs);

  return {
    controller,
    effectiveTimeoutMs,
    didTimeOut: () => timedOut,
    wasExternallyAborted: () => externallyAborted,

    cleanup() {
      clearTimeout(timeoutId);

      if (externalSignal?.removeEventListener) {
        externalSignal.removeEventListener(
          "abort",
          abortFromExternalSignal
        );
      }
    },
  };
}

/* -------------------------- Request helper (core) -------------------------- */
async function request(method, url, data, opts = {}) {
  const {
    headers,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    noBust = false,
    isOwnerApi = false,
    rawResponse = false,
    params,
    signal,
    credentials = "include",
  } = opts || {};

  const requestMethod = String(method || "GET").toUpperCase();
  const urlStr = typeof url === "string" ? url : "";
  const ownerContext = Boolean(
    isOwnerApi ||
      isOwnerApiPath(urlStr)
  );

  const abortContext = createAbortContext(
    signal,
    timeoutMs
  );

  const requestHeaders = {
    Accept: "application/json",
    ...(headers || {}),
  };

  const isFormData =
    typeof FormData !== "undefined" &&
    data instanceof FormData;

  const ownerToken = ownerContext
    ? pickOwnerToken()
    : "";

  const userToken = !ownerContext
    ? pickUserToken()
    : "";

  const tenantSlug = getTenantSlug();
  const tenantId = readTenantIdFromStorage();

  const tenantHeaders =
    !ownerContext &&
    !isNonTenantApiPath(urlStr) &&
    tenantSlug
      ? {
          "X-Tenant-Slug": tenantSlug,
          ...(tenantId
            ? {
                "X-Tenant-Id": tenantId,
              }
            : {}),
        }
      : {};

  const tokenHeaders = {};

  if (ownerToken) {
    tokenHeaders.Authorization = `Bearer ${ownerToken}`;
  } else if (userToken) {
    tokenHeaders.Authorization = `Bearer ${userToken}`;
  }

  let body;

  if (
    data != null &&
    !isFormData &&
    requestMethod !== "GET" &&
    requestMethod !== "HEAD"
  ) {
    const contentTypeKey = Object.keys(requestHeaders).find(
      (key) =>
        String(key).toLowerCase() === "content-type"
    );

    if (!contentTypeKey) {
      requestHeaders["Content-Type"] = "application/json";
    }

    body =
      typeof data === "string" &&
      String(
        requestHeaders[
          contentTypeKey || "Content-Type"
        ] || ""
      )
        .toLowerCase()
        .includes("text/")
        ? data
        : JSON.stringify(data);
  } else if (isFormData) {
    body = data;
  }

  const base = joinUrl(url, {
    isOwnerApi: ownerContext,
  });

  const urlObject = appendParams(base, params);

  if (
    (requestMethod === "GET" ||
      requestMethod === "HEAD") &&
    !noBust
  ) {
    urlObject.searchParams.set(
      "cb",
      String(Date.now())
    );
  }

  const fullUrl = urlObject.toString();

  const finalHeaders = {
    ...requestHeaders,
    ...tenantHeaders,
    ...tokenHeaders,
  };

  let response;

  try {
    response = await fetch(fullUrl, {
      method: requestMethod,
      headers: finalHeaders,
      body,
      credentials,
      signal: abortContext.controller.signal,
    });
  } catch (requestError) {
    const timedOut = abortContext.didTimeOut();
    const externallyAborted =
      abortContext.wasExternallyAborted();

    const aborted =
      requestError?.name === "AbortError" ||
      externallyAborted ||
      timedOut;

    let message =
      requestError?.message ||
      "Network error";

    let status = 0;
    let code = "ERR_NETWORK";

    if (timedOut) {
      message =
        `Request timed out after ` +
        `${abortContext.effectiveTimeoutMs} ms`;

      status = 408;
      code = "ETIMEDOUT";
    } else if (
      externallyAborted ||
      aborted
    ) {
      message = "Request canceled";
      code = "ERR_CANCELED";
    }

    if (!externallyAborted) {
      surface(
        `${requestMethod} ${fullUrl} → ` +
          `network error: ${message}`
      );
    }

    const wrappedError = new Error(message);
    wrappedError.cause = requestError;
    wrappedError.status = status;
    wrappedError.code = code;

    if (externallyAborted) {
      wrappedError.name = "AbortError";
    }

    throw wrappedError;
  } finally {
    abortContext.cleanup();
  }

  if (rawResponse) {
    return response;
  }

  const payload =
    await readResponsePayload(response);

  if (!response.ok) {
    const message =
      (payload &&
        typeof payload === "object" &&
        (payload.error || payload.message)) ||
      (typeof payload === "string" &&
        payload) ||
      `Request failed: ${response.status}`;

    surface(
      `${requestMethod} ${fullUrl} → ` +
        `${response.status}: ${message}`
    );

    if (response.status === 401) {
      try {
        if (console.groupCollapsed) {
          console.groupCollapsed(
            "[HTTP UNAUTH]",
            requestMethod,
            fullUrl,
            "status:",
            response.status
          );
        }

        console.log?.("message:", message);
        console.log?.(
          "ownerContext:",
          ownerContext
        );
        console.log?.(
          "tenantSlug:",
          tenantSlug || "(none)"
        );
        console.log?.(
          "tenantId:",
          tenantId || "(none)"
        );

        console.log?.(
          "finalHeaders (auth-related only):",
          {
            Authorization:
              finalHeaders.Authorization
                ? "(present)"
                : "(missing)",
            "X-Tenant-Slug":
              finalHeaders["X-Tenant-Slug"],
            "X-Tenant-Id":
              finalHeaders["X-Tenant-Id"],
          }
        );

        console.trace?.(
          "Stack at unauthorized response"
        );
        console.groupEnd?.();
      } catch {}

      dispatchUnauthorized(
        ownerContext,
        {
          status: response.status,
          url: fullUrl,
          message,
        }
      );
    }

    const error = new Error(message);
    error.status = response.status;
    error.data = payload;
    error.response = attachPayloadToResponse(
      response,
      payload
    );
    error.rawResponse = response;

    throw error;
  }

  return {
    data: payload,
    status: response.status,
    ok: response.ok,
    headers: response.headers,
    url: response.url,
    response,
  };
}

/* ------------------------------ HTTP interface ----------------------------- */
const http = {
  get: (url, opts) =>
    request("GET", url, null, opts),

  head: (url, opts) =>
    request("HEAD", url, null, opts),

  post: (url, data, opts) =>
    request("POST", url, data, opts),

  put: (url, data, opts) =>
    request("PUT", url, data, opts),

  patch: (url, data, opts) =>
    request("PATCH", url, data, opts),

  delete: (url, opts) =>
    request("DELETE", url, null, opts),

  owner: {
    get: (url, opts) =>
      request(
        "GET",
        url,
        null,
        {
          ...(opts || {}),
          isOwnerApi: true,
        }
      ),

    post: (url, data, opts) =>
      request(
        "POST",
        url,
        data,
        {
          ...(opts || {}),
          isOwnerApi: true,
        }
      ),

    put: (url, data, opts) =>
      request(
        "PUT",
        url,
        data,
        {
          ...(opts || {}),
          isOwnerApi: true,
        }
      ),

    patch: (url, data, opts) =>
      request(
        "PATCH",
        url,
        data,
        {
          ...(opts || {}),
          isOwnerApi: true,
        }
      ),

    delete: (url, opts) =>
      request(
        "DELETE",
        url,
        null,
        {
          ...(opts || {}),
          isOwnerApi: true,
        }
      ),
  },

  raw: {
    get: (url, opts) =>
      request(
        "GET",
        url,
        null,
        {
          ...(opts || {}),
          rawResponse: true,
        }
      ),

    head: (url, opts) =>
      request(
        "HEAD",
        url,
        null,
        {
          ...(opts || {}),
          rawResponse: true,
        }
      ),

    post: (url, data, opts) =>
      request(
        "POST",
        url,
        data,
        {
          ...(opts || {}),
          rawResponse: true,
        }
      ),

    put: (url, data, opts) =>
      request(
        "PUT",
        url,
        data,
        {
          ...(opts || {}),
          rawResponse: true,
        }
      ),

    patch: (url, data, opts) =>
      request(
        "PATCH",
        url,
        data,
        {
          ...(opts || {}),
          rawResponse: true,
        }
      ),

    delete: (url, opts) =>
      request(
        "DELETE",
        url,
        null,
        {
          ...(opts || {}),
          rawResponse: true,
        }
      ),
  },

  // Return an absolute URL using the same tenant/API-base rules.
  // Used by file/upload helpers; does not perform a network request.
  open: (url, opts) =>
    joinUrl(url, opts || {}),

  clearUserToken() {
    try {
      const maybeClearStaffPayloads = (store) => {
        try {
          [
            "loggedInUser",
            "cmfm_user",
            "cmfmUser",
          ].forEach((key) => {
            const raw = store.getItem(key);
            if (!raw) return;

            const payload = safeJsonParse(raw);
            const role =
              readRoleFromPayload(payload);

            if (role === "citizen") return;

            store.removeItem(key);
          });

          store.removeItem("role");
        } catch {}
      };

      const keys = [
        "authToken",
        "token",
        "jwt",
        "accessToken",
        "cmfmToken",
        "cmfm_token",
        "tenant_token",
        "tenantToken",
        "portal_token",
        "portalToken",
        "cmfm_jwt",
      ];

      keys.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {}

        try {
          sessionStorage.removeItem(key);
        } catch {}
      });

      maybeClearStaffPayloads(localStorage);
      maybeClearStaffPayloads(sessionStorage);
    } catch {}
  },

  clearOwnerToken() {
    try {
      localStorage.removeItem("ownerToken");
    } catch {}

    try {
      sessionStorage.removeItem("ownerToken");
    } catch {}

    try {
      localStorage.removeItem(
        "mededtekOwnerToken"
      );
    } catch {}

    try {
      sessionStorage.removeItem(
        "mededtekOwnerToken"
      );
    } catch {}
  },
};

export default http;

/* --------------------------- Named JSON helpers --------------------------- */
export const getJSON = (url, opts) =>
  http
    .get(url, opts)
    .then((response) => response?.data);

export const headJSON = (url, opts) =>
  http
    .head(url, opts)
    .then((response) => response?.data);

export const postJSON = (
  url,
  data,
  opts
) =>
  http
    .post(url, data, opts)
    .then((response) => response?.data);

export const putJSON = (
  url,
  data,
  opts
) =>
  http
    .put(url, data, opts)
    .then((response) => response?.data);

export const patchJSON = (
  url,
  data,
  opts
) =>
  http
    .patch(url, data, opts)
    .then((response) => response?.data);

export const delJSON = (url, opts) =>
  http
    .delete(url, opts)
    .then((response) => response?.data);

/* ------------------------------ Useful exports ---------------------------- */
export {
  joinUrl,
  API_BASE,
};
